import { Injectable } from '@angular/core';

import { normalizeProvinceCode } from '../../../../utils/province-code';
import { CustomerAddress, NationalIdDocumentType } from '../../../model/customer-information.model';
import { CustomerVehicleRelationType } from '../../../model/customer-vehicle.model';
import { FoundCustomer } from '../../../model/found-customer.model';
import {
  EsoCrmAddress,
  EsoCrmCustomerData,
  EsoCrmCustomerDataResponse,
  EsoCrmCustomerMainType,
  EsoCrmCustomerVehicleRelationType,
  EsoCrmDocumentType,
  EsoCrmPreferredInformation,
  EsoCrmPreferredPhone,
} from '../model/response.model';

@Injectable({
  providedIn: 'root',
})
export class EsoCrmFoundCustomerViewModelMapper {
  mapToFoundCustomers(customerDataResponse: EsoCrmCustomerDataResponse): FoundCustomer[] {
    return customerDataResponse.map((response) => this.mapToFoundCustomer(response));
  }

  mapToFoundCustomer(data: EsoCrmCustomerData): FoundCustomer {
    return {
      gcid: data.gcid,
      accountNumber: undefined, // Indicates that the customer was loaded from ESO CRM
      bmwMiniId: data.bmwMiniId,
      firstName: data.individualName?.firstName,
      middleName: data.individualName?.middleName,
      lastName: data.individualName?.lastName,
      suffix: data.individualName?.suffix,
      phone: this.getPhone(data),
      mobile: this.getMobile(data),
      email: this.getPreferredEmail(data.preferredEmail, data),
      address: this.getAddress(data),
      isIndividual: data.customerMainType === EsoCrmCustomerMainType.PERSON,
      customerToVehicleRelations: this.mapVehicleCustomerRelationType(data.role),
      taxNumber: data.taxNumber,
      nationalId1: data.individualDetails?.nationalId1 ?? undefined,
      nationalId1DocumentType: this.mapNationalIdDocumentType(data.documentType),
      inSyncWithDirectSales: data.inSyncWithDirectSales,
    };
  }

  mapAddress(address: EsoCrmAddress | undefined): CustomerAddress | undefined {
    if (!address) {
      return undefined;
    }

    return {
      street: address.street,
      city: address.city,
      houseNumber: address.houseNumber,
      postalCode: address.postCode,
      province: normalizeProvinceCode(address.state, address.country),
      country: address.country,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
    };
  }

  mapVehicleCustomerRelationType(
    relationType: EsoCrmCustomerVehicleRelationType | undefined,
  ): Set<CustomerVehicleRelationType> | undefined {
    switch (relationType) {
      case EsoCrmCustomerVehicleRelationType.USER:
        return new Set([CustomerVehicleRelationType.USER]);
      case EsoCrmCustomerVehicleRelationType.OWNER:
        return new Set([CustomerVehicleRelationType.OWNER]);
      case EsoCrmCustomerVehicleRelationType.KEEPER:
        return new Set([CustomerVehicleRelationType.KEEPER]);
      default:
        return new Set();
    }
  }

  getMobile(data: EsoCrmCustomerData): string | undefined {
    const prefInformation = data.preferredPhone;

    if (
      prefInformation === EsoCrmPreferredPhone.BUSINESS_MOBILE &&
      data.individualCommunications?.businessCommunications?.mobilePhone
    ) {
      return data.individualCommunications?.businessCommunications?.mobilePhone;
    } else if (
      prefInformation === EsoCrmPreferredPhone.PRIVATE_MOBILE &&
      data.individualCommunications?.privateCommunications?.mobilePhone
    ) {
      return data.individualCommunications?.privateCommunications?.mobilePhone;
    }
    return (
      data.individualCommunications?.businessCommunications?.mobilePhone ??
      data.individualCommunications?.privateCommunications?.mobilePhone
    );
  }

  getPhone(data: EsoCrmCustomerData): string | undefined {
    const prefInformation = data.preferredPhone;

    if (
      prefInformation === EsoCrmPreferredPhone.BUSINESS_LANDLINE &&
      data.individualCommunications?.businessCommunications?.landlinePhone
    ) {
      return data.individualCommunications?.businessCommunications?.landlinePhone;
    } else if (
      prefInformation === EsoCrmPreferredPhone.PRIVATE_LANDLINE &&
      data.individualCommunications?.privateCommunications?.landlinePhone
    ) {
      return data.individualCommunications?.privateCommunications?.landlinePhone;
    }
    return (
      data.individualCommunications?.businessCommunications?.landlinePhone ??
      data.individualCommunications?.privateCommunications?.landlinePhone
    );
  }

  getAddress(data: EsoCrmCustomerData): CustomerAddress | undefined {
    if (
      data.individualAddresses?.preferredAddress === EsoCrmPreferredInformation.BUSINESS &&
      data.individualAddresses.businessAddress
    ) {
      return this.mapAddress(data.individualAddresses.businessAddress);
    } else if (
      data.individualAddresses?.preferredAddress === EsoCrmPreferredInformation.PRIVATE &&
      data.individualAddresses.privateAddress
    ) {
      return this.mapAddress(data.individualAddresses.privateAddress);
    } else {
      // Return first non-empty-address
      const address = data.individualAddresses?.businessAddress ?? data.individualAddresses?.privateAddress;
      return this.mapAddress(address);
    }
  }

  getPreferredEmail(
    prefInformation: EsoCrmPreferredInformation | undefined,
    data: EsoCrmCustomerData,
  ): string | undefined {
    if (
      prefInformation === EsoCrmPreferredInformation.BUSINESS &&
      data.individualCommunications?.businessCommunications?.email
    ) {
      return data.individualCommunications?.businessCommunications?.email;
    } else if (
      prefInformation === EsoCrmPreferredInformation.PRIVATE &&
      data.individualCommunications?.privateCommunications?.email
    ) {
      return data.individualCommunications?.privateCommunications?.email;
    } else {
      return (
        data.individualCommunications?.businessCommunications?.email ??
        data.individualCommunications?.privateCommunications?.email
      );
    }
  }

  mapNationalIdDocumentType(esoDocumentType: EsoCrmDocumentType | undefined): NationalIdDocumentType | undefined {
    switch (esoDocumentType) {
      case EsoCrmDocumentType.PER_IDE_DRIVER_LICENCE:
        return NationalIdDocumentType.DRIVER_LICENCE;
      case EsoCrmDocumentType.PER_IDE_DRIVER_LICENSE_PASSPORT_ID:
        return NationalIdDocumentType.DRIVER_LICENSE_ID;
      case EsoCrmDocumentType.PER_IDE_IDENTITY_CARD:
        return NationalIdDocumentType.IDENTITY_CARD;
      case EsoCrmDocumentType.PER_IDE_IDENTIFICATION_NUMBER:
        return NationalIdDocumentType.IDENTIFICATION_NUMBER;
      case EsoCrmDocumentType.PER_IDE_PASSPORT:
        return NationalIdDocumentType.PASSPORT;
      case EsoCrmDocumentType.PER_IDE_PASSPORT_ID:
        return NationalIdDocumentType.PASSPORT_ID;
      case EsoCrmDocumentType.PER_IDE_PERSON_ID:
        return NationalIdDocumentType.PERSON_ID;
      case EsoCrmDocumentType.PER_IDE_SOCIAL_SECURITY_NUMBER:
        return NationalIdDocumentType.SOCIAL_SECURITY_NUMBER;
      case EsoCrmDocumentType.PER_IDE_SOUTH_AFRICA_NATIONAL_ID:
        return NationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID;
      case undefined:
        return undefined;
    }
  }
}
